import { type ClassValue, clsx } from "clsx";
import { format } from "date-fns";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import { logger } from "@/lib/logger";

/**
 * Combines multiple class names and Tailwind classes efficiently
 * Handles conditional classes, merges Tailwind utility classes properly
 * @param inputs - Class name(s) to combine
 * @returns Merged class string
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function decodeJwt(token: string) {
  try {
    const base64Payload = token.split(".")[1];
    const payload = JSON.parse(Buffer.from(base64Payload, "base64").toString());
    return payload;
  } catch (error) {
    logger.error("Error decoding JWT:", error);
    return null;
  }
}

/**
 * Formats a date string or Date object into "MM/DD/YYYY Year" format (e.g., "01/25/2025").
 * @param date - The date to format (string or Date object).
 * @returns Formatted date string or an empty string if the input is invalid.
 */
export function formatDateNumericDayMonthYear(
  date: string | Date | undefined | null
): string {
  if (!date) return "";
  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      logger.error(
        "Invalid date provided to formatDateNumericDayMonthYear:",
        undefined,
        { data: { date } }
      );
      return "";
    }
    return dateObj.toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
    });
  } catch (error) {
    logger.error("Error formatting date:", error);
    return "";
  }
}

/**
 * Formats a date string or Date object into "Today", "Yesterday", "Last week", "Last month", "Last year", or a standard date format.
 * @param date - The date to format (string or Date object).
 * @returns Formatted date string or an empty string if the input is invalid.
 */
export function formatDateToRelativeString(
  date: string | Date | undefined | null
): string {
  if (!date) return "";
  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      logger.error(
        "Invalid date provided to formatDateToRelativeString:",
        undefined,
        { data: { date } }
      );
      return "";
    }

    const now = new Date();

    // Normalize dates to the start of the day for accurate comparison
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const inputDateNormalized = new Date(
      dateObj.getFullYear(),
      dateObj.getMonth(),
      dateObj.getDate()
    );

    // Today
    if (inputDateNormalized.getTime() === today.getTime()) {
      return "Today";
    }

    // Yesterday
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (inputDateNormalized.getTime() === yesterday.getTime()) {
      return "Yesterday";
    }

    return format(dateObj, "MMMM dd");
  } catch (error) {
    logger.error("Error formatting date in formatDateToRelativeString:", error);
    return "";
  }
}

/**
 * Safely converts a date value to a Date object.
 * Handles various input types: Date objects, date strings, and unknown values.
 * @param dateValue - The date value to parse (Date, string, or unknown)
 * @param fallbackDate - Optional fallback date if parsing fails (defaults to current date)
 * @returns A valid Date object
 */
export function parseDateValue(
  dateValue: Date | string | unknown,
  fallbackDate: Date = new Date()
): Date {
  if (dateValue instanceof Date) {
    return dateValue;
  }

  if (typeof dateValue === "string") {
    const parsed = new Date(dateValue);
    return isNaN(parsed.getTime()) ? fallbackDate : parsed;
  }

  return fallbackDate;
}

/**
 * Extracts the initials from a given name.
 * @param name - The name to extract initials from.
 * @returns The initials of the name, or an empty string if the name is undefined.
 */
export function getInitials(name: string | undefined): string {
  if (!name) return "";

  const initials = name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return initials;
}

/**
 * Generates a complete name from the given name and last name.
 * @param name - The name of the user
 * @param lastName - The last name of the user
 * @returns The complete name in the format "Name Lastname" or an empty string if both are undefined.
 */
export function getCompleteName(
  name: string | undefined,
  lastName: string | undefined
): string {
  if (!name && !lastName) return "";
  return `${name || ""} ${lastName || ""}`.trim();
}

/**
 * Truncates a string to a specified maximum length, appending a suffix if necessary.
 * @param str - The string to truncate.
 * @param maxLength - The maximum length of the string, including the suffix.
 * @param suffix - The suffix to append if the string is truncated (default is "...").
 * @returns The truncated string, or the original string if it is shorter than or equal to maxLength.
 */
export function truncateString(
  str: string,
  maxLength: number,
  suffix: string = "..."
): string {
  if (!str || str.length <= maxLength) return str;
  return str.slice(0, maxLength - suffix.length) + suffix;
}

/** RFC 4122 UUID format (v1-v5): 8-4-4-4-12 hex. */
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Validates if a string is a valid UUID (RFC 4122).
 * @param value - The string to validate.
 * @returns true if valid UUID.
 */
export function isValidUuid(value: string): boolean {
  return typeof value === "string" && UUID_REGEX.test(value.trim());
}

/**
 * Validates if a string is a valid URL format (strict - requires full protocol).
 * @param value - The string to validate as a URL.
 * @param fieldName - The name of the field being validated (for error messages).
 * @returns Error message string if invalid, null if valid.
 */
export function validateUrlFormat(
  value: string,
  fieldName: string
): string | null {
  try {
    new URL(value);
    return null; // Valid URL
  } catch {
    return `${fieldName} must be a valid URL (e.g., https://example.com)`;
  }
}

/**
 * Normalizes website URLs to ensure they have the proper format and protocol.
 * Handles various input formats: full URLs with protocol, www.domain.com, or simple domain.com.
 *
 * @param url - The website URL to normalize
 * @returns Normalized website URL with https:// protocol
 *
 * @example
 * normalizeWebsiteUrl("ramp.com") // "https://ramp.com"
 * normalizeWebsiteUrl("www.brex.com") // "https://www.brex.com"
 * normalizeWebsiteUrl("https://tropicapp.io") // "https://tropicapp.io"
 */
export function normalizeWebsiteUrl(url: string): string {
  if (!url) return "";

  const trimmedUrl = url.trim();

  // If it's already a valid URL with protocol, return as is
  if (trimmedUrl.startsWith("http://") || trimmedUrl.startsWith("https://")) {
    return trimmedUrl;
  }

  // Add https:// to any other format (www.domain.com or domain.com)
  return `https://${trimmedUrl}`;
}

/**
 * Validates if a string is a valid website URL format (flexible - accepts domains without protocol).
 *
 * @param url - The website URL to validate
 * @param fieldName - The name of the field being validated (for error messages)
 * @returns Error message string if invalid, null if valid
 *
 * @example
 * validateWebsiteUrl("ramp.com", "Website") // null (valid)
 * validateWebsiteUrl("invalid", "Website") // "Website must be a valid URL..."
 */
export function validateWebsiteUrl(
  url: string,
  fieldName: string
): string | null {
  if (!url) {
    return `${fieldName} is required`;
  }

  const trimmedUrl = url.trim();

  // Check for basic domain format (allows subdomains, paths, etc.)
  // This regex validates:
  // - Optional protocol (http:// or https://)
  // - Optional www.
  // - Domain name with at least one dot
  // - Optional port, path, query params
  const urlPattern =
    /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)$/;

  if (!urlPattern.test(trimmedUrl)) {
    return `${fieldName} must be a valid URL (e.g., example.com, www.example.com, or https://example.com)`;
  }

  // Additional validation: try to construct a URL object with the normalized URL
  try {
    const normalizedUrl = normalizeWebsiteUrl(trimmedUrl);
    new URL(normalizedUrl);
    return null; // Valid URL
  } catch {
    return `${fieldName} must be a valid URL (e.g., example.com, www.example.com, or https://example.com)`;
  }
}

// Copy `text` to the clipboard with a "Copied!" toast on success.
// Uses the async Clipboard API when available, falls back to a hidden
// textarea + document.execCommand("copy") for contexts where
// `navigator.clipboard` is undefined (older Safari, sandboxed iframes,
// plain HTTP origins, etc.). Surfaces an error toast if both paths fail
// so the user is never left wondering whether the copy worked.
export const copyToClipboard = async (
  text: string,
  successDescription?: string
) => {
  const showSuccess = () =>
    toast.success("Copied!", {
      description: successDescription || text,
      duration: 1500,
    });
  const showFailure = () =>
    toast.error("Couldn't copy", {
      description: "Your browser blocked clipboard access.",
      duration: 2000,
    });

  // Preferred path: async Clipboard API (requires a secure context).
  try {
    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function"
    ) {
      await navigator.clipboard.writeText(text);
      showSuccess();
      return;
    }
  } catch {
    // Fall through to the legacy path.
  }

  // Legacy fallback. Two pitfalls we have to dodge:
  //   1. Radix Dialog / Sheet traps focus inside its SheetContent. A
  //      textarea appended to `document.body` is outside that trap, so
  //      `.focus()` / `.select()` never take effect and execCommand
  //      runs against an empty selection. Append into the dialog when
  //      one is open so the textarea inherits the focus trap.
  //   2. Some browsers refuse to copy from an `opacity: 0` element.
  //      Render the textarea offscreen with real dimensions instead.
  try {
    const previousActive = document.activeElement as HTMLElement | null;
    const dialogRoot =
      previousActive?.closest('[role="dialog"]') ??
      document.querySelector('[role="dialog"]') ??
      document.body;

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "0";
    textarea.style.left = "-9999px";
    textarea.style.width = "1px";
    textarea.style.height = "1px";
    textarea.style.padding = "0";
    textarea.style.border = "none";

    dialogRoot.appendChild(textarea);
    textarea.focus({ preventScroll: true });
    textarea.select();
    textarea.setSelectionRange(0, text.length);

    const ok = document.execCommand("copy");
    textarea.remove();

    if (previousActive && typeof previousActive.focus === "function") {
      previousActive.focus({ preventScroll: true });
    }

    if (!ok) throw new Error("document.execCommand('copy') returned false");
    showSuccess();
  } catch {
    showFailure();
  }
};

/**
 * Gets a date with the specified number of days in the past
 * @param {number} days - Number of days to go back
 * @returns {Date} Calculated date
 */
export const getDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date;
};

/**
 * Counts items created after a specific date
 * @param {Array} items - Array of items with createdAt property
 * @param {Date} fromDate - Date from which to count
 * @returns {number} Number of items
 */
export const countItemsSince = (items, fromDate) => {
  if (!items?.length) return 0;

  return items.filter((item) => {
    const createdDate = new Date(item.createdAt);
    return createdDate >= fromDate;
  }).length;
};

/**
 * Counts items from the last N days
 * @param {Array} items - Array of items with createdAt property
 * @param {number} days - Number of days to go back (default 7)
 * @returns {number} Number of items
 */
export const countRecentItems = (items, days = 7) => {
  const fromDate = getDaysAgo(days);
  return countItemsSince(items, fromDate);
};

/**
 * Gets the current application domain without protocol
 * @returns The domain (e.g., "localhost:3000" or "mi-app.com")
 */
export const getAppDomain = (): string => {
  if (typeof window !== "undefined") {
    return window.location.host;
  }
  return process.env.APP_DOMAIN || "localhost:3000";
};

/**
 * Gets the current application URL with protocol
 * @returns The full URL (e.g., "http://localhost:3000" or "https://mi-app.com")
 */
export const getAppUrl = (): string => {
  if (typeof window !== "undefined") {
    return window.location.origin;
  }
  return process.env.APP_URL || "http://localhost:3000";
};

/**
 * Extracts plain text from TipTap JSON content
 *
 * This function handles various TipTap content structures including:
 * - Standard TipTap document structure with paragraphs and text nodes
 * - Text nodes with formatting marks (bold, italic, etc.)
 * - Nested content structures
 * - Edge cases where content might be stored differently
 */
export const extractTextFromTipTap = (
  content: Record<string, unknown> | string | undefined
): string => {
  if (!content) return "";
  if (typeof content === "string") return content;

  try {
    // Recursively extract text from TipTap JSON structure
    const extractText = (node: unknown): string => {
      if (typeof node === "string") return node;
      if (typeof node === "number") return String(node);
      if (!node || typeof node !== "object") return "";

      const obj = node as Record<string, unknown>;

      // If it's a text node, return its text content (regardless of marks)
      if (obj.type === "text" && typeof obj.text === "string") {
        return obj.text;
      }

      // If it has content array, process each child and join with spaces
      if (Array.isArray(obj.content)) {
        return obj.content
          .map(extractText)
          .filter((text) => text.length > 0) // Remove empty strings
          .join(" ");
      }

      // If it has text property, return it
      if (typeof obj.text === "string") {
        return obj.text;
      }

      // Handle edge cases: check for other possible text properties
      if (typeof obj.value === "string") {
        return obj.value;
      }

      return "";
    };

    const result = extractText(content);

    // Clean up extra whitespace and normalize
    return result.replace(/\s+/g, " ").trim();
  } catch (error) {
    logger.error("Error extracting text from TipTap content:", error, {
      data: {
        contentType: typeof content,
      },
    });
    return "";
  }
};
